import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { chipRows, queryFromState, refine, removeChip, stateFromQuery, toggle } from '@/browse/browse_state'
import type { BrowseState, BrowseStateConfig } from '@/browse/browse_state'

export interface BrowseNavigation {
  // A change the page makes on its own, such as choosing a default, replaces the history entry,
  // so going back skips the URL the page redirected from rather than landing on it and redirecting again.
  replace?: boolean
}

// The route query is the only copy of the filters: every change is a navigation that adds
// a history entry, so reload, shared links and the back button all restore exactly what
// was shown.
export function useBrowseState(config: BrowseStateConfig) {
  const route = useRoute()
  const router = useRouter()

  const state = computed(() => stateFromQuery(config, route.query))

  function navigate(next: BrowseState, { replace = false }: BrowseNavigation = {}) {
    const location = { path: route.path, query: queryFromState(config, next, route.query) }
    if (replace) {
      return router.replace(location)
    }
    return router.push(location)
  }

  return {
    state,
    chipRows: computed(() => chipRows(config, state.value)),
    refine: (key: string, values: string[], navigation?: BrowseNavigation) => navigate(refine(config, state.value, key, values), navigation),
    toggle: (key: string, value: string) => navigate(toggle(config, state.value, key, value)),
    removeChip: (key: string, value: string) => navigate(removeChip(config, state.value, key, value)),
    chipRowsAfterRemoving: (key: string, value: string) => chipRows(config, removeChip(config, state.value, key, value)),
    setKeyword: (keyword: string) => navigate({ ...state.value, keyword })
  }
}
